export function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
}

export function formatCurrency(amount) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function getDaysUntil(dateStr) {
  const target = new Date(dateStr);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  const diff = target - now;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

export function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function getPaymentStatus(expense) {
  if (expense.isPaid) return 'paid';
  const days = getDaysUntil(expense.dueDate);
  if (days < 0) return 'overdue';
  if (days <= 14) return 'due-soon';
  return 'upcoming';
}

export function getTodoStatus(todo) {
  if (todo.done) return 'done';
  if (!todo.deadline) return 'none';
  const diffMs = new Date(todo.deadline).getTime() - Date.now();
  if (diffMs < 0) return 'overdue';
  if (diffMs <= 24 * 60 * 60 * 1000) return 'due-soon'; // within 24 hours
  return 'upcoming';
}

export function getTripTotals(trip) {
  const expenseTotal = trip.expenses.reduce((sum, e) => sum + e.amount, 0);
  const totalCost = trip.totalBudget > 0 ? trip.totalBudget : expenseTotal;
  const totalPaid = trip.expenses.filter(e => e.isPaid).reduce((sum, e) => sum + e.amount, 0);
  const outstanding = Math.max(0, totalCost - totalPaid);
  const progress = totalCost > 0 ? (totalPaid / totalCost) * 100 : 0;
  return { totalCost, totalPaid, outstanding, progress };
}

export function getTripStatus(trip) {
  const { totalCost, totalPaid } = getTripTotals(trip);
  if (totalCost > 0 && totalPaid >= totalCost) return 'all-paid';
  const hasOverdue = trip.expenses.some(e => !e.isPaid && getDaysUntil(e.dueDate) < 0);
  if (hasOverdue) return 'overdue';
  const hasDueSoon = trip.expenses.some(e => !e.isPaid && getDaysUntil(e.dueDate) <= 14 && getDaysUntil(e.dueDate) >= 0);
  if (hasDueSoon) return 'due-soon';
  return 'upcoming';
}

export function getMotivationalMessage(trip) {
  const { progress, outstanding } = getTripTotals(trip);
  const days = getDaysUntil(trip.departureDate);
  const unpaidCount = trip.expenses.filter(e => !e.isPaid).length;

  if (progress >= 100) return "All paid! Time to get excited! 🎉";
  if (progress >= 80) return `You're ${Math.round(progress)}% there! Almost done!`;
  if (unpaidCount === 1) return "Just 1 payment left! You've got this!";
  if (unpaidCount <= 3) return `Only ${unpaidCount} payments to go!`;
  if (days <= 30 && days > 0) return `${days} days to go — the countdown is on!`;
  if (days <= 7 && days > 0) return "Nearly time! So exciting! ✈️";
  return `${formatCurrency(outstanding)} to go — you can do it!`;
}

export function getMonthsUntil(dateStr) {
  if (!dateStr) return 0;
  const target = new Date(dateStr);
  const now = new Date();
  const months = (target.getFullYear() - now.getFullYear()) * 12 + (target.getMonth() - now.getMonth());
  // If we're past this month's payday-ish date, don't count the current month
  return Math.max(0, months);
}

export function getPerMonth(outstanding, deadlineDate) {
  if (!deadlineDate || outstanding <= 0) return null;
  const months = getMonthsUntil(deadlineDate);
  if (months <= 0) return { amount: outstanding, months: 0, label: 'due now' };
  return { amount: outstanding / months, months, label: `over ${months} month${months !== 1 ? 's' : ''}` };
}

export const TRIP_TYPES = [
  { id: 'holiday', label: 'Holiday', icon: '🌴' },
  { id: 'short-break', label: 'Short Break', icon: '🧳' },
  { id: 'overnight', label: 'Overnight Stay', icon: '🌙' },
  { id: 'day-trip', label: 'Day Trip', icon: '☀️' },
];

export const DESTINATION_EMOJIS = [
  '🏖️', '🏔️', '🏰', '🎢', '🌴', '🗼', '🏛️', '🌊',
  '⛷️', '🎭', '🌺', '🦁', '🐠', '🏜️', '🗽', '🎪',
  '🚢', '🏕️', '🌅', '🎿',
];

export const MEMBER_COLOURS = [
  '#a855f7', '#14b8a6', '#f97316', '#eab308',
  '#ec4899', '#3b82f6', '#10b981', '#ef4444',
];
